import { Plus, PaperPlaneTilt, CaretRight, ArrowLineUp, GearSix } from '@phosphor-icons/react';
import { getAsset, type AssetCode } from '@onyx/shared';
import { useMe, useWallet, useConfig, useActivity } from '../lib/queries';
import { useFiat, usePortfolio } from '../lib/app';
import { useLang, useT } from '../lib/i18n';
import { cryptoParts, displayDigits, fiatParts, formatCrypto, formatFiat } from '../lib/format';
import { navigate } from '../lib/router';
import { usePrefs, setPref } from '../lib/prefs';
import { AssetChip } from '../components/AssetChip';
import { StoriesRow } from '../components/Stories';
import { Avatar, ErrorState, IconButton, Money, Row, Section, Skeleton, Slab } from '../components/ui';
import { ActivityRow } from './History';

export function Home() {
  const t = useT();
  const lang = useLang();
  const fiat = useFiat();
  const me = useMe();
  const config = useConfig();
  const wallet = useWallet();
  const recent = useActivity({}, 3);
  const prefs = usePrefs();
  const { total, held, list } = usePortfolio(wallet.data, fiat);

  const rows = prefs.hideZero && held.length ? held : list;
  const empty = wallet.isSuccess && held.length === 0;
  const second = fiat === 'USD' ? 'RUB' : 'USD';
  const secondTotal = list.reduce((s, h) => s + h.amount * (wallet.data?.prices[h.asset]?.[second] ?? 0), 0);

  return (
    <main className="screen home">
      <header className="home__top">
        <Avatar user={me.data?.me ?? null} size={48} />
        <h1 className="home__hello">
          {t['home.hello']}, {me.data?.me.firstName ?? ' '}
        </h1>
        <IconButton label={t['settings.title']} className="icon-btn--round" onClick={() => navigate('/settings')}>
          <GearSix size={22} weight="fill" />
        </IconButton>
      </header>

      <StoriesRow label={t['home.stories']} />

      <section className="card balance" aria-live="polite">
        {config.data?.testnet && <span className="tag tag--warn balance__tag">{t['deposit.testnet']}</span>}
        {wallet.isPending ? (
          <Skeleton w={200} h={44} r={12} />
        ) : (
          <Money parts={fiatParts(total, fiat, lang)} size="display" />
        )}
        <p className="balance__sub num">{wallet.isPending ? ' ' : `≈ ${formatFiat(secondTotal, second, lang)}`}</p>
        <Slab icon={<Plus size={20} weight="bold" />} onClick={() => navigate('/deposit')}>
          {t['home.topUp']}
        </Slab>
      </section>

      <button type="button" className="hero-action" onClick={() => navigate('/send')} disabled={empty}>
        <PaperPlaneTilt className="hero-action__icon" size={26} weight="fill" aria-hidden="true" />
        <span className="hero-action__text">
          <span className="hero-action__title">{t['home.sendFriend']}</span>
          <span className="hero-action__sub">{t['home.sendFriendSub']}</span>
        </span>
        <CaretRight size={22} weight="bold" aria-hidden="true" />
      </button>

      <button type="button" className="card soft-row" onClick={() => navigate('/withdraw')} disabled={empty}>
        <span className="soft-row__icon">
          <ArrowLineUp size={20} weight="bold" aria-hidden="true" />
        </span>
        <span className="soft-row__text">
          <span className="soft-row__title">{t['home.withdraw']}</span>
          <span className="soft-row__sub">{t['home.withdrawSub']}</span>
        </span>
        <CaretRight size={20} weight="bold" className="soft-row__chev" aria-hidden="true" />
      </button>

      {wallet.isError ? (
        <ErrorState text={t['common.error']} onRetry={() => void wallet.refetch()} />
      ) : empty ? (
        <section className="card empty-card">
          <h2>{t['home.emptyTitle']}</h2>
          <p>{t['home.emptyBody']}</p>
        </section>
      ) : (
        <Section
          title={t['home.assets']}
          action={
            held.length > 0 && (
              <button type="button" className="link-btn" onClick={() => setPref('hideZero', !prefs.hideZero)}>
                {prefs.hideZero ? t['home.allAssets'] : t['home.hideZero']}
              </button>
            )
          }
        >
          <div className="rows">
            {wallet.isPending
              ? Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="row">
                    <span className="row__lead">
                      <Skeleton w={44} h={44} r={22} />
                    </span>
                    <span className="row__main">
                      <Skeleton w={80} h={14} />
                      <Skeleton w={120} h={12} />
                    </span>
                  </div>
                ))
              : rows.map((h) => {
                  const def = getAsset(h.asset)!;
                  const locked = Number(h.locked) > 0;
                  return (
                    <Row
                      key={h.asset}
                      onClick={() => navigate(`/asset/${h.asset}`)}
                      leading={<AssetChip asset={h.asset as AssetCode} size={44} />}
                      title={def.name}
                      wrapSub={locked}
                      subtitle={
                        locked ? (
                          <span className="num">
                            <span className="nobreak">
                              {formatCrypto(h.available, displayDigits(Number(h.available), def.stable), lang)} {t['asset.availableShort']} ·
                            </span>{' '}
                            <span className="is-warn nobreak">
                              {formatCrypto(h.locked, displayDigits(Number(h.locked), def.stable), lang)} {t['asset.locked'].toLowerCase()}
                            </span>
                          </span>
                        ) : (
                          <span className="num">
                            {formatFiat(h.price, fiat, lang)}
                            {def.networks.length > 1 && ` · ${t['home.networks'](def.networks.length)}`}
                          </span>
                        )
                      }
                      trailing={<Money parts={cryptoParts(String(h.amount), displayDigits(h.amount, def.stable), lang)} unit={h.asset} size="md" />}
                      trailingSub={<span className="num">{formatFiat(h.value, fiat, lang)}</span>}
                    />
                  );
                })}
          </div>
        </Section>
      )}

      {recent.data && recent.data.pages[0]!.items.length > 0 && (
        <Section
          title={t['history.title']}
          action={
            <button type="button" className="link-btn" onClick={() => navigate('/history')}>
              {t['common.all']}
            </button>
          }
        >
          <div className="rows">
            {recent.data.pages[0]!.items.map((a) => (
              <ActivityRow key={a.id} item={a} />
            ))}
          </div>
        </Section>
      )}
    </main>
  );
}
